import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Calendar, Zap, Loader2, Database, CheckCircle, AlertCircle } from 'lucide-react';
import { useTenants } from '@/hooks/useTenants';
import { useSchedules, useAutomationRules } from '@/hooks/useScheduling';
import { Schedule, AutomationRule } from '@/services/api/scheduling';

import { SchedulesList } from './SchedulesList';
import { ScheduleFormDialog } from './ScheduleFormDialog';
import { ScheduleExecutionHistoryDrawer } from './ScheduleExecutionHistoryDrawer';
import { AutomationRulesList } from './AutomationRulesList';
import { AutomationRuleFormDialog } from './AutomationRuleFormDialog';
import { ExecutionHistoryDrawer } from './ExecutionHistoryDrawer';

interface SchedulingTabProps {
  defaultTenantId?: string;
}

export const SchedulingTab: React.FC<SchedulingTabProps> = ({ defaultTenantId }) => {
  // Active tab state
  const [activeTab, setActiveTab] = useState<'schedules' | 'rules'>('schedules');

  // Tenant filter - use '__all__' as sentinel value since Radix Select doesn't allow empty strings
  const [selectedTenant, setSelectedTenant] = useState<string>(defaultTenantId || '__all__');

  // Dialog states
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] = useState<Schedule | null>(null);
  const [scheduleHistoryDrawerOpen, setScheduleHistoryDrawerOpen] = useState(false);
  const [scheduleForHistory, setScheduleForHistory] = useState<Schedule | null>(null);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<AutomationRule | null>(null);
  const [ruleHistoryDrawerOpen, setRuleHistoryDrawerOpen] = useState(false);
  const [ruleForHistory, setRuleForHistory] = useState<AutomationRule | null>(null);

  // Data fetching - convert '__all__' back to undefined for API calls
  const { data: tenants, isLoading: tenantsLoading, error: tenantsError } = useTenants();
  const actualTenantId = selectedTenant === '__all__' ? undefined : selectedTenant;
  const { data: schedulesData } = useSchedules({ tenant_id: actualTenantId });
  const { data: rulesData } = useAutomationRules({ tenant_id: actualTenantId });

  const schedulesCount = schedulesData?.schedules?.length || 0;
  const rulesCount = rulesData?.rules?.length || 0;

  // Handlers
  const handleCreateSchedule = () => {
    setScheduleToEdit(null);
    setScheduleDialogOpen(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setScheduleToEdit(schedule);
    setScheduleDialogOpen(true);
  };

  const handleViewScheduleHistory = (schedule: Schedule) => {
    setScheduleForHistory(schedule);
    setScheduleHistoryDrawerOpen(true);
  };

  const handleCreateRule = () => {
    setRuleToEdit(null);
    setRuleDialogOpen(true);
  };

  const handleEditRule = (rule: AutomationRule) => {
    setRuleToEdit(rule);
    setRuleDialogOpen(true);
  };

  const handleViewRuleHistory = (rule: AutomationRule) => {
    setRuleForHistory(rule);
    setRuleHistoryDrawerOpen(true);
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center gap-3'>
        <div className='p-3 bg-primary/10 rounded-lg'>
          <Calendar className='h-6 w-6 text-primary' />
        </div>
        <div>
          <h2 className='text-2xl font-bold'>Automatic Scheduling</h2>
          <p className='text-muted-foreground'>
            Configure schedules for automatic data collection and VIN processing
          </p>
        </div>
      </div>

      {/* Tenant Filter - Compact */}
      <div className='flex items-center gap-4 p-4 bg-muted/30 rounded-lg border'>
        <div className='flex items-center gap-2 min-w-0 flex-1'>
          <Database className='h-4 w-4 text-muted-foreground flex-shrink-0' />
          <Label htmlFor='tenant-selector' className='text-sm font-medium whitespace-nowrap'>
            Tenant:
          </Label>
          {tenantsLoading ? (
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-3 w-3 animate-spin' />
              <span>Loading...</span>
            </div>
          ) : tenantsError ? (
            <div className='text-sm text-destructive'>Failed to load tenants</div>
          ) : (
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger id='tenant-selector' className='w-full max-w-xs h-8'>
                <SelectValue placeholder='All tenants' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='__all__'>All tenants</SelectItem>
                {tenants?.map(tenant => (
                  <SelectItem key={tenant.id} value={tenant.id}>
                    <div className='flex items-center gap-2'>
                      <span className='font-medium'>{tenant.name}</span>
                      {tenant.slug && (
                        <span className='text-xs text-muted-foreground'>({tenant.slug})</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {selectedTenant && selectedTenant !== '__all__' ? (
          <Badge
            variant='outline'
            className='bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 flex-shrink-0'
          >
            <CheckCircle className='h-3 w-3 mr-1' />
            Filtered
          </Badge>
        ) : (
          <Badge
            variant='outline'
            className='bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 flex-shrink-0'
          >
            <AlertCircle className='h-3 w-3 mr-1' />
            All Tenants
          </Badge>
        )}
      </div>

      {/* Sub-tabs: Schedules | Automation Rules */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'schedules' | 'rules')}>
        <TabsList className='grid w-full grid-cols-2'>
          <TabsTrigger value='schedules' className='flex items-center gap-2'>
            <Calendar className='h-4 w-4' />
            Schedules
            {schedulesCount > 0 && (
              <Badge variant='secondary' className='ml-1 text-xs'>
                {schedulesCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='rules' className='flex items-center gap-2'>
            <Zap className='h-4 w-4' />
            Automation Rules
            {rulesCount > 0 && (
              <Badge variant='secondary' className='ml-1 text-xs'>
                {rulesCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value='schedules' className='mt-6'>
          <SchedulesList
            tenantId={actualTenantId}
            onEdit={handleEditSchedule}
            onViewHistory={handleViewScheduleHistory}
            onCreateNew={handleCreateSchedule}
          />
        </TabsContent>

        <TabsContent value='rules' className='mt-6'>
          <AutomationRulesList
            tenantId={actualTenantId}
            onEdit={handleEditRule}
            onViewHistory={handleViewRuleHistory}
            onCreateNew={handleCreateRule}
          />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ScheduleFormDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        schedule={scheduleToEdit}
        defaultTenantId={actualTenantId}
      />

      <AutomationRuleFormDialog
        open={ruleDialogOpen}
        onOpenChange={setRuleDialogOpen}
        rule={ruleToEdit}
        defaultTenantId={actualTenantId}
      />

      <ScheduleExecutionHistoryDrawer
        open={scheduleHistoryDrawerOpen}
        onOpenChange={setScheduleHistoryDrawerOpen}
        schedule={scheduleForHistory}
      />

      <ExecutionHistoryDrawer
        open={ruleHistoryDrawerOpen}
        onOpenChange={setRuleHistoryDrawerOpen}
        rule={ruleForHistory}
      />
    </div>
  );
};

export default SchedulingTab;
